// resolveMatchedFlight — pure selection of the best-matching flight from API data.
//
// Extracted from `FlightLookup.js` (module-level `resolveMatchedFlight`) as part
// of the behavior-preserving refactor (issue #240). It first asks the
// FlightAware matcher for a smart match; if none is found it falls back to the
// earliest flight (by `scheduled_out` → `scheduled_off` → `estimated_out`), and
// returns `null` when there are no flights at all.
//
// The source constructed `new FlightAwareService()` internally and called
// `service.parseFlightData(filteredData, targetFlight)`. To keep this function
// pure and React/service-free, the matcher is INJECTED as the `parseFlightData`
// parameter. The container (rewired in task 9.7) supplies a bound
// `FlightAwareService#parseFlightData`, preserving the exact runtime behavior.
//
// Totality: the earliest-flight sort and the `length > 0` / final `null` fallback
// mirror the source exactly. No guards are added around `rawData.flights` beyond
// the source's own `length` check.
//
// React-free and side-effect-free (given a pure-enough injected matcher).
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

/**
 * Resolves the best matching flight from API data.
 *
 * @param {Object} filteredData - Airport-filtered API data passed to the matcher.
 * @param {Object} rawData - The unfiltered API data, used for the earliest-flight fallback.
 * @param {Object} targetFlight - The target flight context for smart matching.
 * @param {(data: Object, target: Object) => (Object|null)} parseFlightData - Injected
 *   FlightAware matcher (e.g. `FlightAwareService#parseFlightData`).
 * @returns {Object|null} The matched flight, the earliest fallback flight, or `null`.
 */
export function resolveMatchedFlight(filteredData, rawData, targetFlight, parseFlightData) {
  const matchedFlight = parseFlightData(filteredData, targetFlight);

  if (matchedFlight) return matchedFlight;

  // Fallback to earliest flight only when no airports were specified
  if (rawData.flights.length > 0) {
    const sortedFlights = [...rawData.flights].sort((a, b) => {
      const timeA = new Date(a.scheduled_out || a.scheduled_off || a.estimated_out || 0).getTime();
      const timeB = new Date(b.scheduled_out || b.scheduled_off || b.estimated_out || 0).getTime();
      return timeA - timeB;
    });
    return sortedFlights[0];
  }

  return null;
}

export default resolveMatchedFlight;
